import type { OrganizationPermission } from "@/api/organizations";
import type { ProjectPermission } from "@/api/projects";
import { teamApi, type Team, type TeamId } from "@/api/teams";
import type { UserId } from "@/api/users";
import { ensureTeams, readCachedTeams } from "@/queries/teams";
import { organizationTeamsQueryOptions } from "@/queries/organizations";
import { ensureQueryData } from "@/queries/utils";
import { isDefined } from "@/utils";
import { AxiosError } from "axios";
import { defineStore } from "pinia";

// The team list lives in the query cache; getters read it reactively and
// mutations refetch it so every consumer sees the server's response.
export const useTeamsStore = defineStore("teams", {
    getters: {
        teams(): Map<TeamId, Team> | undefined {
            const teams = readCachedTeams();
            return teams ? new Map(teams.map((t) => [t.id, t])) : undefined;
        },
        users(): Map<UserId, TeamId[]> | undefined {
            const teams = readCachedTeams();
            if (!teams) return undefined;

            const users = new Map<UserId, TeamId[]>();
            teams.forEach((t) => {
                t.users.forEach((u) => {
                    const memberOf = users.get(u);
                    if (memberOf) {
                        memberOf.push(t.id);
                    } else {
                        users.set(u, [t.id]);
                    }
                });
            });
            return users;
        },
        get() {
            return (id: TeamId): Readonly<Team | undefined> => this.teams?.get(id);
        },
        getTeams() {
            return (id: UserId): Readonly<Team[] | undefined> =>
                this.users
                    ?.get(id)
                    ?.map((t) => this.teams?.get(t))
                    .filter(isDefined);
        },
    },
    actions: {
        async load(force = false) {
            await this.loadTeams(force);
        },
        async loadTeams(force = false) {
            await ensureTeams(force);
        },
        // The team list has no live query consumers, so a mutation must refetch
        // it rather than just mark it stale.
        async refreshTeams() {
            await ensureTeams(true);
        },

        async addUser(id: TeamId, users: UserId[]) {
            await teamApi.addUsers(id, users);
            await this.refreshTeams();
        },
        async removeUser(id: TeamId, user: UserId) {
            await teamApi.removeUser(id, user);
            await this.refreshTeams();
        },

        async deleteTeam(id: TeamId) {
            const team = this.teams?.get(id);

            try {
                await teamApi.delete(id);
            } catch (ex) {
                if (ex instanceof AxiosError) {
                    return ex.response?.data as string;
                }

                throw ex;
            }

            await this.refreshTeams();
            if (team) {
                await ensureQueryData(organizationTeamsQueryOptions(team.organizationId), true);
            }

            return;
        },

        async setPermissions(id: TeamId, permissions: ProjectPermission[]) {
            await teamApi.setPermissions(id, permissions);
            await this.refreshTeams();
        },

        async setOrganizationProjectPermissions(id: TeamId, permissions: ProjectPermission[]) {
            await teamApi.setOrganizationProjectPermissions(id, permissions);
            await this.refreshTeams();
        },

        async setOrganizationPermissions(id: TeamId, permissions: OrganizationPermission[]) {
            await teamApi.setOrganizationPermissions(id, permissions);
            await this.refreshTeams();
        },
    },
});
